/**
 * Ventana publica de reserva (ADR-013).
 *
 * Dos limites independientes:
 *   - **Anticipacion minima**: aritmetica pura de instantes. No interviene la zona.
 *   - **Ventana**: dias de calendario de `America/Montevideo`, incluyendo hoy. Con el
 *     valor inicial de 7, se puede reservar hasta el final del dia `hoy + 6`.
 *
 * `ahora` llega como parametro. El dominio no lee el reloj (CLAUDE.md §1.2).
 *
 * El **panel** autenticado no pasa por aca: empleado y propietario pueden cargar
 * reservas en cualquier fecha, incluso pasada (ADR-013).
 */
import { sumarDiasLocales } from '../tiempo/civil.js';
import { sumarMinutos } from '../tiempo/instante.js';
import { fechaLocalDesdeInstante, finDelDiaLocal } from '../tiempo/zona.js';
import { errorDominio } from '../tipos/errores.js';
import { exito, fallo } from '../tipos/resultado.js';
/** Valores iniciales de la especificacion. */
export const VENTANA_PUBLICA_INICIAL = {
    ventanaPublicaDias: 7,
    anticipacionMinimaMinutos: 5,
};
function validarParametros(parametros) {
    if (!Number.isInteger(parametros.ventanaPublicaDias) || parametros.ventanaPublicaDias < 1) {
        return errorDominio('PARAMETRO_INVALIDO', 'ventanaPublicaDias');
    }
    if (!Number.isInteger(parametros.anticipacionMinimaMinutos) ||
        parametros.anticipacionMinimaMinutos < 0) {
        return errorDominio('PARAMETRO_INVALIDO', 'anticipacionMinimaMinutos');
    }
    return null;
}
/**
 * Calcula los extremos de la ventana publica en un instante dado.
 *
 * `desdeUtc` es el primer inicio aceptable y `hastaUtc` el ultimo, los dos inclusive.
 */
export function calcularVentanaPublica(ahoraUtc, parametros, zona) {
    const invalido = validarParametros(parametros);
    if (invalido !== null) {
        return fallo(invalido);
    }
    const desdeUtc = sumarMinutos(ahoraUtc, parametros.anticipacionMinimaMinutos);
    // "7 dias incluyendo hoy" son hoy y los 6 siguientes: hoy + (7 - 1).
    const hoy = fechaLocalDesdeInstante(ahoraUtc, zona);
    const ultimoDia = sumarDiasLocales(hoy, parametros.ventanaPublicaDias - 1);
    const hasta = finDelDiaLocal(ultimoDia, zona);
    if (!hasta.ok) {
        return hasta;
    }
    return exito({ desdeUtc, hastaUtc: hasta.valor });
}
/**
 * Valida que un inicio de reserva caiga dentro de la ventana publica.
 *
 * Devuelve la ventana calculada cuando pasa, para poder mostrarla en la interfaz sin
 * recalcularla.
 */
export function validarInicioEnVentanaPublica(inicioReservaUtc, ahoraUtc, parametros, zona) {
    const ventana = calcularVentanaPublica(ahoraUtc, parametros, zona);
    if (!ventana.ok) {
        return ventana;
    }
    // Exactamente la anticipacion minima alcanza; un milisegundo menos, no.
    if (inicioReservaUtc < ventana.valor.desdeUtc) {
        return fallo(errorDominio('ANTICIPACION_INSUFICIENTE', 'inicioUtc'));
    }
    if (inicioReservaUtc > ventana.valor.hastaUtc) {
        return fallo(errorDominio('FUERA_DE_VENTANA', 'inicioUtc'));
    }
    return exito(ventana.valor);
}
//# sourceMappingURL=ventana.js.map